import { MouseEventHandler, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import { Box, Typography } from '@mui/material';

import { useAppDispatch } from 'state/hooks';
import { setRMCreateRouteModal } from 'state/routeMonitoringSlice';
import { setWorkflow } from 'state/workflowSlice';

import { WorkflowType } from 'appConstants';
import FontAwesomeIcon, {
  FontAwesomeIconType,
} from 'features/common/FontAwesomeIcon';
import { StlButton } from 'features/common/StlButton/StlButton';
import { background_input } from 'theme/cemTheme';

import { useNotificationProject } from '../useNotificationProject';

import styles from './EmptyState.module.css';

interface EmptyStateProps {
  iconName: string;
  iconType?: FontAwesomeIconType;
  title: string;
  description: string;
  buttonText?: string;
  buttonIcon?: ReactNode;
  onButtonClick?: MouseEventHandler<HTMLButtonElement>;
}

export function EmptyState({
  iconName,
  iconType,
  title,
  description,
  buttonText,
  buttonIcon,
  onButtonClick,
}: EmptyStateProps) {
  return (
    <Box className={styles.emptyStateContainer}>
      <Box className={styles.emptyStateContent}>
        <FontAwesomeIcon
          type={iconType}
          name={iconName}
          size="4rem"
          sx={{ color: background_input }}
        />
        <Typography variant="h4" className={styles.emptyStateText}>
          {title}
        </Typography>
        <Typography variant="caption" className={styles.emptyStateText}>
          {description}
        </Typography>
        {(buttonText || buttonIcon) && (
          <StlButton
            handleClick={onButtonClick}
            isActive
            bold
            color="secondary"
            icon={buttonIcon}
            value={buttonText}
            sx={{
              width: 'auto',
              minWidth: '4rem',
              '&.MuiButton-root': {
                padding: '0.5rem',
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export function NoRoutes() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleCreateRoute = () => {
    dispatch(setWorkflow(WorkflowType.RouteMonitoring));
    dispatch(setRMCreateRouteModal(true));
    navigate('/app?workflow=RM', { replace: true });
  };

  return (
    <EmptyState
      iconName="route"
      iconType="duotone"
      title="Your project has no routes."
      description="Create one to start receiving alert notifications."
      buttonText="Create New Route"
      buttonIcon={<AddIcon />}
      onButtonClick={handleCreateRoute}
    />
  );
}

export function NoAlerts() {
  const { navigateTo } = useNotificationProject();
  const handleCreateAlert = () => navigateTo.newAlert();

  return (
    <EmptyState
      iconName="bell-plus"
      iconType="duotone-regular"
      title="You haven't set any alerts yet."
      description="Create one to get notified about important updates."
      buttonText="Create Alert"
      buttonIcon={<AddIcon />}
      onButtonClick={handleCreateAlert}
    />
  );
}

export function NoResults({ resourceName }: { resourceName: string }) {
  return (
    <EmptyState
      iconType="duotone-regular"
      iconName="magnifying-glass"
      title="No results found"
      description={`No ${resourceName} match your search, try a different phrase.`}
    />
  );
}

export function NoNotifications() {
  return (
    <EmptyState
      iconType="duotone-regular"
      iconName="bell-on"
      title="Your alerts are active"
      description="We will notify you when new activity is detected."
    />
  );
}
